// The agent requests as Blueprint draws them (BL-189; the owner-agents prototype), once for both pages that
// show them: Accounting › Agent requests (the books' own) and Inbox › Agents and approvals › Approvals
// (every request). useApprovalRequests gives ListPage its views (Waiting, Approved, Declined) and its
// region: what's shown, counted, a page at a time (approvals.list). Each request is a RowList row: what it
// does and its money kind, who asked and when, then RequestBody folded, as To approve draws one: the flag,
// why, exactly what will run in a row that opens in place, and the decision. Approving runs it as the
// agent; the toast then says how the run went. Riverstone keeps ApprovalsInbox.tsx's cards; the words are
// approvals-model.ts' and inbox-pages-model.ts'. approval-requests.css places the next page, tokens only.
import type { AgentRequestProps } from '@flowology/ui-web';
import { Link, RowList, type Row } from '@flowology/ui-web/fl';
import { ListPage, type ListPageProps, type ListRegion } from '@flowology/ui-web/patterns';
import { useEffect, useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import { usePaged } from '../blueprint/paged.ts';
import { agentName, invoiceIdsIn } from '../books/model.ts';
import type { Approval } from '../books/types.ts';
import { useAct, type Act } from '../books/ui.tsx';
import { requestProps, type Decision } from '../inbox/inbox-pages-model.ts';
import { agentKeyOf, followApproved, type ApprovalPage, type ApprovalStatus } from './approvals-model.ts';
import { RequestBody } from './RequestBody.tsx';
import { useSaveKey } from './save-key.ts';
import './approval-requests.css';

const SHOWS: readonly { id: ApprovalStatus; label: string; title: string; empty: string }[] = [
  {
    id: 'pending',
    label: 'Waiting',
    title: 'Waiting for a person',
    empty: 'All caught up. New requests appear here.',
  },
  { id: 'approved', label: 'Approved', title: 'Approved', empty: 'Nothing decided this way yet.' },
  { id: 'declined', label: 'Declined', title: 'Declined', empty: 'Nothing decided this way yet.' },
];

/** Why a request can't be decided: the server's word, or that its agent is stopped. */
export function lockedWhy(a: Approval, stopped: string | null): string | null {
  return (
    a.cannotDecide ??
    (stopped
      ? `Nothing can run as the ${agentName(a.agent).toLowerCase()} now: ${stopped.charAt(0).toLowerCase()}${stopped.slice(1).replace(/\.$/, '')}.`
      : null)
  );
}

/** One request's decision as the list makes it: approvals.decide through the page's act, its toast, and
 * a key per attempt, so pressing again after a lost answer decides it once (QA-058). */
function RowBody({
  a,
  r,
  act,
  toast,
  after,
  invoices,
}: {
  a: Approval;
  r: AgentRequestProps;
  act: Act;
  toast: (m: string) => void;
  /** Re-reads the list, once an approved request has run. */
  after: () => void;
  invoices: { id: string; label: string }[];
}) {
  const approveKey = useSaveKey(`approve:${a.id}`);
  const trustKey = useSaveKey(`trust:${a.id}`);
  const declineKey = useSaveKey(`decline:${a.id}`);
  const decide = (d: Decision) => {
    if (d.decision === 'decline') {
      void act.run(
        'approvals.decide',
        { approvalId: a.id, decision: 'decline', ...(d.note ? { note: d.note } : {}) },
        'Declined; the note is kept for the agent.',
        { idempotencyKey: declineKey.key },
      );
      return;
    }
    if (d.trust) {
      void act.run(
        'approvals.decide',
        { approvalId: a.id, decision: 'approve', trustConnection: true },
        'Approved. For the next 12 hours, your AI tool on this connection acts as you without asking.',
        { idempotencyKey: trustKey.key },
      );
      return;
    }
    const who = a.canTrustConnection ? 'you' : `the ${agentName(a.agent).toLowerCase()}`;
    void act
      .run(
        'approvals.decide',
        { approvalId: a.id, decision: 'approve' },
        // not "it runs": it may still be refused when it runs (quiet hours, say), QA-859
        `Approved: it runs as ${who} now. You’ll see here if it can’t.`,
        { idempotencyKey: approveKey.key },
      )
      .then((ok) => {
        if (ok) void followApproved(a.id, who, toast, after);
      });
  };
  return (
    <RequestBody
      r={r}
      busy={act.busy}
      onDecide={decide}
      fold
      invoices={invoices}
      mayDecline={a.cannotDecide === null}
    />
  );
}

/** One request as a row: its title (the action's own name on hover) and money kind, who asked and when, then RequestBody. */
function requestRow(a: Approval, r: AgentRequestProps, body: ReactNode): Row {
  return {
    id: a.id,
    label: r.title,
    name: <span title={a.action}>{r.title}</span>,
    // its money kind beside the name: an end would take a column of its own, and squeeze the input on a phone
    ...(r.money ? { word: { text: r.money, tone: 'ink' as const } } : {}),
    lines: [{ text: `${r.asker.name}, asked ${r.asked}` }, { part: body }],
  };
}

/** The invoices the requests name, by number (QA-267); one that can't be read keeps its id. */
function useInvoiceNumbers(shown: readonly Approval[]) {
  const [numbers, setNumbers] = useState<ReadonlyMap<string, string>>(new Map());
  const ids = [...new Set(shown.flatMap((a) => invoiceIdsIn(a.input)))].filter((id) => !numbers.has(id));
  const wanted = ids.join(',');
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    void Promise.all(
      wanted.split(',').map((id) =>
        client
          .call<{ number: string }>('invoices.get', { id })
          .then((r) => [id, r.number] as const)
          .catch(() => null),
      ),
    ).then((got) => {
      if (!live) return;
      setNumbers((m) => new Map([...m, ...got.filter((g): g is readonly [string, string] => g !== null)]));
    });
    return () => {
      live = false;
    };
  }, [wanted]);
  return numbers;
}

type RequestsInput = {
  toast: (m: string) => void;
  /** Which requests are shown (by action name); all when absent. */
  filter?: (action: string) => boolean;
  /** Why an agent is stopped (by key), or null. */
  stoppedReason?: (agentKey: string) => string | null;
  /** After a decision (e.g. to refresh counts elsewhere). */
  onChange?: () => void;
  /** The company's zone, for when each was asked and lapses. */
  timeZone?: string;
};

/** The requests as a part under another page's head (Inbox › Agents and approvals' Approvals). */
export function ApprovalRequestsPart(input: RequestsInput) {
  return <ListPage {...useApprovalRequests(input)} />;
}

/** ListPage's views and list for the agent requests: `filter` narrows them (the books' own). */
export function useApprovalRequests({
  toast,
  filter,
  stoppedReason = () => null,
  onChange,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: RequestsInput): Required<Pick<ListPageProps, 'views'>> & { list: ListRegion } {
  const [status, setStatus] = useState<ApprovalStatus>('pending');
  const show = SHOWS.find((s) => s.id === status)!;
  // a page at a time, newest first (Show more reads on)
  const list = usePaged<Approval, ApprovalPage>('approvals.list', { status }, (p) => p.approvals);
  const act = useAct(toast, () => {
    list.reload();
    onChange?.();
  });
  const shown = list.rows?.filter((a) => !filter || filter(a.action)) ?? [];
  const numbers = useInvoiceNumbers(shown);
  // every request: the server's count; narrowed by `filter`, what's shown so far, "N+" while pages remain
  const count = !filter && list.first ? list.first.total : shown.length;
  const more = !!filter && (!!list.more || list.busy);
  const row = (a: Approval) => {
    const r = requestProps(a, { timeZone, names: numbers, stopped: stoppedReason(agentKeyOf(a.agent)) });
    const invoices = invoiceIdsIn(a.input).map((id) => ({
      id,
      label: numbers.get(id) ?? `invoice …${id.slice(-6)}`,
    }));
    return requestRow(
      a,
      r,
      <RowBody key={a.id} a={a} r={r} act={act} toast={toast} after={list.reload} invoices={invoices} />,
    );
  };
  return {
    views: {
      label: 'Show',
      items: SHOWS,
      value: status,
      onChange: (s) => setStatus(s as ApprovalStatus),
    },
    list: {
      title: show.title,
      count,
      ...(more ? { more } : {}),
      what: 'the agent requests',
      status: !list.rows ? (list.error ? 'failed' : 'loading') : shown.length ? 'ready' : 'empty',
      emptyText: show.empty,
      failedText: list.error ?? 'The agent requests didn’t load.',
      onRetry: list.reload,
      children: (
        <RowList label={show.title} rows={shown.map(row)} {...(list.more ? { onMore: list.more } : {})} />
      ),
      // narrowed, the shown ones may all be on pages not read yet: the next page, under the empty sentence
      ...(list.more && list.rows && !shown.length
        ? {
            after: (
              <p className="bp-appr-more">
                <Link onPress={list.more}>Show more</Link>
              </p>
            ),
          }
        : {}),
    },
  };
}
